import {
  customElement,
  html,
  css,
  cssManager,
  property,
  state,
  type TemplateResult,
} from '@design.estate/dees-element';
import { DeesInputBase } from '../dees-input-base/dees-input-base.js';
import '../../00group-utility/dees-icon/dees-icon.js';
import { demoFunc } from './dees-input-tags.demo.js';
import { themeDefaultStyles } from '../../00theme.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-input-tags': DeesInputTags;
  }
}

@customElement('dees-input-tags')
export class DeesInputTags extends DeesInputBase<DeesInputTags> {
  // STATIC
  public static demo = demoFunc;
  public static demoGroups = ['Input'];

  // INSTANCE
  @property({ type: Array })
  accessor value: string[] = [];

  @property({ type: String })
  accessor placeholder: string = 'Add tags...';

  @property({ type: Number })
  accessor maxTags: number = 0; // 0 means unlimited

  @property({ type: Array })
  accessor suggestions: string[] = [];

  @state()
  accessor inputValue: string = '';

  @state()
  accessor showSuggestions: boolean = false;

  @state()
  accessor highlightedSuggestionIndex: number = -1;

  @property({ type: String })
  accessor validationText: string = '';

  public static styles = [
    themeDefaultStyles,
    ...DeesInputBase.baseStyles,
    cssManager.defaultStyles,
    css`
      :host {
        display: block;
        font-family: var(--dees-font-family);
      }

      .input-wrapper {
        width: 100%;
      }

      .tags-container {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 6px;
        padding: 6px 10px;
        min-height: var(--dees-control-height-lg);
        background: transparent;
        border: 1px solid var(--dees-color-border-default);
        border-radius: var(--dees-radius-lg);
        corner-shape: var(--dees-corner-shape);
        transition:
          border-color var(--dees-transition-fast) var(--dees-ease-standard),
          box-shadow var(--dees-transition-fast) var(--dees-ease-standard);
        cursor: text;
      }

      .tags-container:hover:not(.disabled) {
        border-color: var(--dees-color-border-strong);
      }

      .tags-container:focus-within {
        border-color: var(--dees-color-accent-primary);
        box-shadow: 0 0 0 3px var(--dees-color-focus-ring);
      }

      .tags-container.disabled {
        background: var(--dees-color-badge-default-bg);
        border-color: var(--dees-color-border-default);
        cursor: not-allowed;
        opacity: 0.5;
      }

      .tag {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 2px 8px;
        background: color-mix(in srgb, var(--dees-color-text-primary) 10%, transparent);
        color: var(--dees-color-text-secondary);
        border: 1px solid color-mix(in srgb, var(--dees-color-text-primary) 15%, transparent);
        border-radius: var(--dees-radius-xs);
        font-size: 13px;
        font-weight: 500;
        line-height: 18px;
        user-select: none;
        animation: tagAppear var(--dees-transition-fast) var(--dees-ease-standard);
      }

      @keyframes tagAppear {
        from {
          transform: scale(0.95);
          opacity: 0;
        }
        to {
          transform: scale(1);
          opacity: 1;
        }
      }

      .tag-remove {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 14px;
        height: 14px;
        margin-left: 2px;
        border-radius: var(--dees-radius-xs);
        cursor: pointer;
        transition:
          background-color var(--dees-transition-fast) var(--dees-ease-standard),
          color var(--dees-transition-fast) var(--dees-ease-standard);
        color: var(--dees-color-text-muted);
      }

      .tag-remove:hover {
        background: var(--dees-color-active);
        color: var(--dees-color-text-secondary);
      }

      .tag-remove dees-icon {
        width: 10px;
        height: 10px;
      }

      .tag-input {
        flex: 1;
        min-width: 120px;
        border: none;
        background: transparent;
        outline: none;
        font-size: 14px;
        font-family: inherit;
        color: var(--dees-color-text-primary);
        padding: 2px 4px;
        line-height: 20px;
      }

      .tag-input::placeholder {
        color: var(--dees-color-label-quaternary);
      }

      .tag-input:disabled {
        cursor: not-allowed;
      }

      /* Suggestions dropdown */
      .suggestions-container {
        position: relative;
      }

      .suggestions-dropdown {
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        margin-top: 4px;
        background: var(--dees-material-regular-bg);
        -webkit-backdrop-filter: var(--dees-material-regular-filter);
        backdrop-filter: var(--dees-material-regular-filter);
        border: 1px solid var(--dees-color-border-default);
        border-radius: var(--dees-radius-lg);
        corner-shape: var(--dees-corner-shape);
        box-shadow: var(--dees-shadow-lg);
        max-height: 200px;
        overflow-y: auto;
        z-index: 1000;
      }

      @supports not ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px))) {
        .suggestions-dropdown {
          background: var(--dees-material-regular-bg-opaque);
        }
      }

      @media (prefers-reduced-transparency: reduce) {
        .suggestions-dropdown {
          background: var(--dees-material-regular-bg-opaque);
          -webkit-backdrop-filter: none;
          backdrop-filter: none;
        }
      }

      .suggestion {
        padding: 6px 10px;
        cursor: pointer;
        transition: background-color var(--dees-transition-fast) var(--dees-ease-standard);
        font-size: 14px;
        color: var(--dees-color-text-primary);
      }

      .suggestion:hover {
        background: var(--dees-color-hover);
      }

      .suggestion.highlighted {
        background: var(--dees-color-active);
        color: var(--dees-color-text-primary);
      }

      /* Validation styles */
      .validation-message {
        color: var(--dees-color-accent-error);
        font-size: 13px;
        margin-top: 6px;
        line-height: 1.5;
      }

      /* Scrollbar styling */
      .suggestions-dropdown::-webkit-scrollbar {
        width: 8px;
      }

      .suggestions-dropdown::-webkit-scrollbar-track {
        background: transparent;
      }

      .suggestions-dropdown::-webkit-scrollbar-thumb {
        background: var(--dees-color-border-default);
        border-radius: 4px;
      }

      .suggestions-dropdown::-webkit-scrollbar-thumb:hover {
        background: var(--dees-color-border-strong);
      }
    `,
  ];

  public render(): TemplateResult {
    const filteredSuggestions = this.suggestions.filter(
      suggestion => 
        !this.value.includes(suggestion) &&
        suggestion.toLowerCase().includes(this.inputValue.toLowerCase())
    );

    return html`
      <div class="input-wrapper">
        ${this.label ? html`<dees-label .label=${this.label} .required=${this.required}></dees-label>` : ''}
        
        <div class="suggestions-container">
          <div
            class="tags-container ${this.disabled ? 'disabled' : ''}"
            @click=${this.handleContainerClick}
          >
            ${this.value.map(tag => html`
              <div class="tag">
                <span>${tag}</span>
                ${!this.disabled ? html`
                  <div class="tag-remove" @click=${(e: Event) => this.removeTag(e, tag)}>
                    <dees-icon .icon=${'lucide:x'}></dees-icon>
                  </div>
                ` : ''}
              </div>
            `)}
            
            ${!this.disabled && (!this.maxTags || this.value.length < this.maxTags) ? html`
              <input
                type="text"
                class="tag-input"
                .placeholder=${this.placeholder}
                .value=${this.inputValue}
                @input=${this.handleInput}
                @keydown=${this.handleKeyDown}
                @focus=${this.handleFocus}
                @blur=${this.handleBlur}
                ?disabled=${this.disabled}
              />
            ` : ''}
          </div>

          ${this.showSuggestions && filteredSuggestions.length > 0 ? html`
            <div class="suggestions-dropdown">
              ${filteredSuggestions.map((suggestion, index) => html`
                <div
                  class="suggestion ${index === this.highlightedSuggestionIndex ? 'highlighted' : ''}"
                  @mousedown=${(e: Event) => {
                    e.preventDefault(); // Prevent blur
                    this.addTag(suggestion);
                  }}
                  @mouseenter=${() => this.highlightedSuggestionIndex = index}
                >
                  ${suggestion}
                </div>
              `)}
            </div>
          ` : ''}
        </div>

        ${this.validationText ? html`
          <div class="validation-message">${this.validationText}</div>
        ` : ''}
        
        ${this.renderDescription()}
      </div>
    `;
  }

  private handleContainerClick(e: Event) {
    if (this.disabled) return;
    
    const input = this.shadowRoot?.querySelector('.tag-input') as HTMLInputElement;
    if (input && e.target !== input) {
      input.focus();
    }
  }

  private handleInput(e: Event) {
    const input = e.target as HTMLInputElement;
    this.inputValue = input.value;
    
    // Check for comma or semicolon to add tag
    if (this.inputValue.includes(',') || this.inputValue.includes(';')) {
      const tag = this.inputValue.replace(/[,;]/g, '').trim();
      if (tag) {
        this.addTag(tag);
      }
    }
  }

  private handleKeyDown(e: KeyboardEvent) {
    const input = e.target as HTMLInputElement;
    
    if (e.key === 'Enter') {
      e.preventDefault();
      if (this.highlightedSuggestionIndex >= 0 && this.showSuggestions) {
        const filteredSuggestions = this.suggestions.filter(
          suggestion => 
            !this.value.includes(suggestion) &&
            suggestion.toLowerCase().includes(this.inputValue.toLowerCase())
        );
        if (filteredSuggestions[this.highlightedSuggestionIndex]) {
          this.addTag(filteredSuggestions[this.highlightedSuggestionIndex]);
        }
      } else if (this.inputValue.trim()) {
        this.addTag(this.inputValue.trim());
      }
    } else if (e.key === 'Backspace' && !this.inputValue && this.value.length > 0) {
      // Remove last tag when backspace is pressed on empty input
      this.removeTag(e, this.value[this.value.length - 1]);
    } else if (e.key === 'ArrowDown' && this.showSuggestions) {
      e.preventDefault();
      const filteredCount = this.suggestions.filter(
        s => !this.value.includes(s) && s.toLowerCase().includes(this.inputValue.toLowerCase())
      ).length;
      this.highlightedSuggestionIndex = Math.min(
        this.highlightedSuggestionIndex + 1,
        filteredCount - 1
      );
    } else if (e.key === 'ArrowUp' && this.showSuggestions) {
      e.preventDefault();
      this.highlightedSuggestionIndex = Math.max(this.highlightedSuggestionIndex - 1, 0);
    } else if (e.key === 'Escape') {
      this.showSuggestions = false;
      this.highlightedSuggestionIndex = -1;
    }
  }

  private handleFocus() {
    if (this.suggestions.length > 0) {
      this.showSuggestions = true;
    }
  }

  private handleBlur() {
    // Delay to allow click on suggestions
    setTimeout(() => {
      this.showSuggestions = false;
      this.highlightedSuggestionIndex = -1;
    }, 200);
  }

  private addTag(tag: string) {
    if (!tag || this.value.includes(tag)) return;
    if (this.maxTags && this.value.length >= this.maxTags) return;

    this.value = [...this.value, tag];
    this.inputValue = '';
    this.showSuggestions = false;
    this.highlightedSuggestionIndex = -1;
    
    // Clear the input
    const input = this.shadowRoot?.querySelector('.tag-input') as HTMLInputElement;
    if (input) {
      input.value = '';
    }

    this.emitChange();
  }

  private removeTag(e: Event, tag: string) {
    e.stopPropagation();
    this.value = this.value.filter(t => t !== tag);
    this.emitChange();
  }

  private emitChange() {
    this.dispatchEvent(new CustomEvent('change', {
      detail: { value: this.value },
      bubbles: true,
      composed: true
    }));
    this.changeSubject.next(this);
  }

  public getValue(): string[] {
    return this.value;
  }

  public setValue(value: string[]): void {
    this.value = value || [];
  }

  public async validate(): Promise<boolean> {
    if (this.required && (!this.value || this.value.length === 0)) {
      this.validationText = 'At least one tag is required';
      return false;
    }
    this.validationText = '';
    return true;
  }
}